import { css, cx } from "@emotion/css";
import type { ButtonMenuItem, SimpleMenuItem, ToggleMenuItem } from "@octopusdeploy/design-system-components";
import { Dialog, Divider, useCurrentScrollArea, useIsDesktop, useIsMobile } from "@octopusdeploy/design-system-components";
import { borderWidth, colorScales, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { DeploymentProcessResource, RunbookProcessResource, GitRefResource } from "@octopusdeploy/octopus-server-client";
import { ProcessType, StartTrigger, SpecialVariables, Permission, isDeploymentProcessResource, isRunbookProcessResource } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useRef } from "react";
import { createUnassignedTargetTagWarningSettingChangedEvent } from "~/areas/projects/components/Process/ConnectedDeploymentTargetsStatus/UnassignedTargetTagsWarningCallout.analytics";
import type { ProcessEditorSearchFilterResult } from "~/areas/projects/components/Process/Contexts/ProcessSearchFilter";
import { useProcessTemplatesFromContext } from "~/areas/projects/components/Process/Contexts/ProcessTemplatesContextProvider";
import { getProcessTemplateSlugFromAction, getProcessTemplateVersionMaskFromAction } from "~/areas/projects/components/Process/ProcessTemplates/processTemplateId";
import { repository } from "~/clientInstance";
import type { Errors } from "~/components/DataBaseComponent";
import DeleteDialog from "~/components/Dialog/DeleteDialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { StepRolling } from "~/components/Images/Process/StepRolling";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import WarningPanel from "~/components/WarningPanel/WarningPanel";
import { ErrorPanel } from "~/components/form";
import useLocalStorage from "~/hooks/useLocalStorage";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { useOptionalRunbookContext } from "../Runbooks/RunbookContext";
import getActionLogoUrl from "../getActionLogoUrl";
import ArcWarningCallout from "./Common/ArcWarningCallout";
import type { CloneDeploymentProcessStepsSource, CloneRunbookProcessSource } from "./Common/CloneStep";
import CloneStep from "./Common/CloneStep";
import { deleteActionAndRedirect, isMatchingProcessTemplate, processScopedEditPermission } from "./Common/CommonProcessHelpers";
import SelectParentStep from "./Common/SelectParentStep";
import { useActionTemplatesFromContext } from "./Contexts/ProcessActionTemplatesContextProvider";
import { useProcessContext } from "./Contexts/ProcessContext";
import { ProcessErrorSelectors, useProcessErrorActions, useProcessErrorSelectors } from "./Contexts/ProcessErrors/ProcessErrorsContext";
import { useProcessQueryStringContext } from "./Contexts/ProcessQueryString/ProcessQueryStringContext";
import { useProcessSearchFilterContext } from "./Contexts/ProcessSearchFilter/ProcessSearchFilterContext";
import { ProcessWarningSelectors, useProcessWarningActions, useProcessWarningSelectors } from "./Contexts/ProcessWarnings/ProcessWarningsContext";
import deploymentPartStyles from "./ListItems/ProcessListItem.module.less";
import { CloneStepAction, useCreateProcessListItemContextMenu } from "./ListItems/ProcessListItemContextMenu";
import ProcessListItemForSidebar from "./ListItems/ProcessListItemForSidebar";
import deploymentPartForSidebarStyles from "./ListItems/ProcessListItemForSidebar.module.less";
import StepSorter from "./ListItems/ProcessListItemSorter";
import type { ProcessSidebarElement, ProcessSidebarRootStep } from "./ProcessSidebar/ProcessSidebar";
import { ProcessSidebar } from "./ProcessSidebar/ProcessSidebar";
import { type ProcessSidebarStepActionData } from "./ProcessSidebar/ProcessSidebarStep";
import type { ProcessIdentifier, StoredAction, StoredStep, Warnings } from "./types";
import { isProjectTemplateProcessIdentifier, isTemplatedProjectProcessIdentifier, isDeploymentOrRunbookProcessIdentifier, isDeploymentProcessIdentifier, isProcessTemplateProcessIdentifier, isRunbookProcessIdentifier } from "./types";
interface ProcessSidebarLayoutProps {
    render(): React.ReactNode;
    gitRefResource: GitRefResource | undefined;
    processIdentifier: ProcessIdentifier;
    releaseCreationPackageStepId: string | undefined;
}
const DeploymentProcessLayoutOverflowMenu: React.FC = () => {
    const processContext = useProcessContext();
    const processIdentifier = processContext.state.processIdentifier;
    const isImprovedTargetFeedbackFeatureEnabled = useOctopusFeatureToggle("improved-target-feedback", false);
    const showUnassignedTargetTagWarningToggle = isImprovedTargetFeedbackFeatureEnabled && isAllowed({ permission: Permission.EnvironmentView, environment: "*" }) && isAllowed({ permission: Permission.MachineView });
    const projectId = isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined;
    const [showUnassignedTargetTag, setShowUnassignedTargetTagWarning] = useLocalStorage(`Octopus.Project.${projectId}.ShowUnassignedTargetTagWarning`, true);
    const warningActions = useProcessWarningActions();
    const trackEvent = useTrackEvent();
    const overFlowActions = React.useMemo(() => {
        if (isTemplatedProjectProcessIdentifier(processIdentifier)) {
            return [];
        }
        const toggleShowUnassignedTargetTagWarning = () => {
            warningActions.clearWarnings();
            trackEvent(createUnassignedTargetTagWarningSettingChangedEvent(!showUnassignedTargetTag));
            setShowUnassignedTargetTagWarning(!showUnassignedTargetTag);
        };
        return [
            OverflowMenuItems.dialogItem("Reorder steps", <StepSorter title="Reorder steps" initialItems={processContext.selectors.getAllSteps()} onComplete={(process) => {
                    processContext.actions.reorderSteps(process.map((x) => x.Id));
                    processContext.actions.resetPackageRequirementAfterPackageAcquisitionStep();
                }}/>, {
                permission: processScopedEditPermission(processIdentifier.type),
                project: isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined,
                wildcard: true,
            }),
            ...(showUnassignedTargetTagWarningToggle ? [OverflowMenuItems.toggleItem("Show unassigned target tag warnings", showUnassignedTargetTag, () => toggleShowUnassignedTargetTagWarning())] : []),
        ];
    }, 
    //TODO: Create a smart component for step sorter in order to avoid changing menu items unncessarily as
    //we don't really need to know about the steps and actions until the dialog is actually shown. These menu items
    //will re-render if our state changes, which is unwanted.
    [processContext.actions, processContext.selectors, processIdentifier, setShowUnassignedTargetTagWarning, showUnassignedTargetTag, showUnassignedTargetTagWarningToggle, warningActions, trackEvent]);
    return React.useMemo(() => <OverflowMenu key="reorder" menuItems={overFlowActions}/>, [overFlowActions]);
};
DeploymentProcessLayoutOverflowMenu.displayName = "DeploymentProcessLayoutOverflowMenu"
function useProcessSidebarMenuItems(openStepSorterDialog: () => void): SimpleMenuItem[] {
    const processContext = useProcessContext();
    const processIdentifier = processContext.state.processIdentifier;
    const isImprovedTargetFeedbackFeatureEnabled = useOctopusFeatureToggle("improved-target-feedback", false);
    const showUnassignedTargetTagWarningToggle = isImprovedTargetFeedbackFeatureEnabled && isAllowed({ permission: Permission.EnvironmentView, environment: "*" }) && isAllowed({ permission: Permission.MachineView });
    const projectId = isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined;
    const [showUnassignedTargetTag, setShowUnassignedTargetTagWarning] = useLocalStorage(`Octopus.Project.${projectId}.ShowUnassignedTargetTagWarning`, true);
    const warningActions = useProcessWarningActions();
    const trackEvent = useTrackEvent();
    const openStepSorterDialogMenuItem: ButtonMenuItem = {
        type: "button",
        label: "Reorder steps",
        onClick: openStepSorterDialog,
    };
    const toggleUnassignedTargetTagsMenuItem: ToggleMenuItem = {
        type: "toggle",
        label: "Show unassigned target tag warnings",
        isEnabled: showUnassignedTargetTag,
        setIsEnabled: (isEnabled) => {
            warningActions.clearWarnings();
            trackEvent(createUnassignedTargetTagWarningSettingChangedEvent(isEnabled));
            setShowUnassignedTargetTagWarning(isEnabled);
        },
    };
    const menuItems: SimpleMenuItem[] = [];
    if (isAllowed({
        permission: processScopedEditPermission(processIdentifier.type),
        project: isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined,
        wildcard: true,
    }) &&
        !isTemplatedProjectProcessIdentifier(processIdentifier)) {
        menuItems.push(openStepSorterDialogMenuItem);
    }
    if (showUnassignedTargetTagWarningToggle) {
        menuItems.push(toggleUnassignedTargetTagsMenuItem);
    }
    return menuItems;
}
interface ActionSidebarItemProps {
    actionId: string;
    isCurrent: boolean;
    onShowContextMenu: (event: React.MouseEvent<Element, MouseEvent>, actionId: string) => void;
}
const ActionSidebarItem: React.FC<ActionSidebarItemProps> = (props) => {
    const { onShowContextMenu } = props;
    const { selectors } = useProcessContext();
    const actionTemplates = useActionTemplatesFromContext();
    const action = selectors.getActionById(props.actionId);
    const step = selectors.getStepById(action.ParentId);
    const isChildAction = selectors.isChildAction(action.Id);
    const stepNumber = selectors.getStepNumber(step.Id);
    const actionNumber = selectors.getActionNumber(action.Id);
    let actionTypeName = action.ActionType;
    const actionTemplate = actionTemplates.find((x) => x.Type === action.ActionType);
    if (actionTemplate) {
        actionTypeName = actionTemplate.Name;
    }
    const { actions: queryStringActions } = useProcessQueryStringContext();
    const onClick = React.useCallback(() => {
        queryStringActions.showProcessAction(action.Id);
        window.scroll({ top: 0, left: 0, behavior: "smooth" });
        //The current query string actions are tied to the same context as selectors. In order to avoid onClick breaking
        //memoization, we should move the actions into its own separate context.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.Id]);
    const icon = React.useMemo(() => <StepRolling height="1.6rem" className={deploymentPartStyles.stepIcon}/>, []);
    const processErrorSelectors = useProcessErrorSelectors();
    const processWarningSelectors = useProcessWarningSelectors();
    //TODO: revisit, we should remove the selectors from `getActionErrors` and consider passing in the result of the associated selectors instead to avoid
    //having to get a new set of errors every time process state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const errors = React.useMemo(() => processErrorSelectors.getActionErrors(action.Id, selectors), [action.Id, processErrorSelectors]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const warnings = React.useMemo(() => processWarningSelectors.getActionWarningSummaries(action.Id, selectors), [action.Id, processWarningSelectors]);
    const handleShowContextMenu = React.useCallback((event: React.MouseEvent<Element, MouseEvent>) => {
        onShowContextMenu(event, action.Id);
    }, [action.Id, onShowContextMenu]);
    return (<ProcessListItemForSidebar actionType={actionTypeName} logoUrl={getActionLogoUrl(action)} icon={icon} isCurrentAction={props.isCurrent} isParentGroup={false} actionErrors={errors} actionWarnings={warnings} index={`${isChildAction ? `${stepNumber}.${actionNumber}.` : `${stepNumber}.`}`} isDisabled={selectors.isActionDisabled(action.Id)} notes={action.Notes} name={action.Name} isRunInParallelWithLast={step.StartTrigger === StartTrigger.StartWithPrevious && !selectors.isChildAction(action.Id) && !selectors.isFirstStep(step.Id)} onShowContextMenu={handleShowContextMenu} onClick={onClick}/>);
};
ActionSidebarItem.displayName = "ActionSidebarItem"
interface ParentStepSidebarItemProps {
    stepId: string;
    isCurrent: boolean;
    onShowContextMenu: (event: React.MouseEvent<Element, MouseEvent>, stepId: string) => void;
    renderChildAction: (action: StoredAction) => React.ReactNode;
}
const ParentStepSidebarItem: React.FC<ParentStepSidebarItemProps> = ({ stepId, isCurrent, onShowContextMenu, renderChildAction }) => {
    const { selectors } = useProcessContext();
    const step = selectors.getStepById(stepId);
    const maxParallelism = step.Properties[SpecialVariables.Action.MaxParallelism];
    const showWindowSize = maxParallelism ? maxParallelism.toString().length > 0 : false;
    const { actions: queryStringActions } = useProcessQueryStringContext();
    const onClick = React.useCallback(() => {
        queryStringActions.showProcessParentStep(step.Id);
        window.scroll({ top: 0, left: 0, behavior: "smooth" });
        //The current query string actions are tied to the same context as selectors. In order to avoid onClick breaking
        //memoization, we should move the actions into its own separate context.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step.Id]);
    const parentStepLabel = React.useMemo(() => showWindowSize ? (<span>Rolling deployment</span>) : (<span>
                    Multi-step deployment across
                    <br />
                    deployment targets
                </span>), [showWindowSize]);
    const icon = React.useMemo(() => <StepRolling height="1.6rem" className={deploymentPartStyles.stepIcon}/>, []);
    const processErrorSelectors = useProcessErrorSelectors();
    const errors = React.useMemo(() => processErrorSelectors.getStepErrors(step.Id), [processErrorSelectors, step.Id]);
    const processWarningSelectors = useProcessWarningSelectors();
    const warnings = React.useMemo(() => processWarningSelectors.getStepWarningSummaries(step.Id), [processWarningSelectors, step.Id]);
    const handleShowContextMenu = React.useCallback((event: React.MouseEvent<Element, MouseEvent>) => {
        onShowContextMenu(event, step.Id);
    }, [onShowContextMenu, step.Id]);
    return (<div key={step.Id} className={deploymentPartForSidebarStyles.group}>
            <ProcessListItemForSidebar actionType={parentStepLabel} icon={icon} isCurrentAction={isCurrent} isParentGroup={true} actionErrors={errors} actionWarnings={warnings} index={`${selectors.getStepNumber(step.Id)}.`} isDisabled={selectors.isStepDisabled(step.Id)} name={step.Name} isRunInParallelWithLast={step.StartTrigger === StartTrigger.StartWithPrevious && !selectors.isFirstStep(step.Id)} onShowContextMenu={handleShowContextMenu} onClick={onClick} notes={null}/>
            {selectors.getChildActions(step.Id).map((action) => {
            return renderChildAction(action);
        })}
        </div>);
};
ParentStepSidebarItem.displayName = "ParentStepSidebarItem"
const useProcessErrors = (filteredSteps: ProcessEditorSearchFilterResult, errorSelectors: ProcessErrorSelectors) => {
    const { selectors } = useProcessContext();
    const { actions: queryStringActions } = useProcessQueryStringContext();
    return React.useMemo(() => {
        const stepsWithErrors = errorSelectors.getStepsWithErrors();
        const actionsWithErrors = errorSelectors.getActionsWithErrors();
        const errors = new Map<string, {
            message: string;
            onClick: () => void;
        }>();
        filteredSteps.steps.forEach(({ step: filteredStep }) => {
            const step = selectors.getStepById(filteredStep.Id);
            if (stepsWithErrors.includes(step.Id)) {
                const stepNumber = selectors.getStepNumber(step.Id);
                const linkId = step.ActionIds.length === 1 ? step.ActionIds[0] : step.Id;
                // Link to action if there is only 1 action, link to parent step if multiple actions
                errors.set(linkId, {
                    message: `${stepNumber}. ${step.Name}`,
                    onClick: () => {
                        step.ActionIds.length === 1 ? queryStringActions.showProcessAction(step.ActionIds[0]) : queryStringActions.showProcessParentStep(step.Id);
                        window.scroll({ top: 0, left: 0, behavior: "smooth" });
                    },
                });
            }
            step.ActionIds.forEach((actionId) => {
                if (actionsWithErrors.includes(actionId)) {
                    const action = selectors.getActionById(actionId);
                    const stepNumber = selectors.getStepNumber(step.Id);
                    const actionNumber = selectors.getActionNumber(actionId);
                    const isChildAction = selectors.isChildAction(actionId);
                    const index = isChildAction ? `${stepNumber}.${actionNumber}` : stepNumber;
                    errors.set(actionId, {
                        message: `${index}. ${action.Name}`,
                        onClick: () => {
                            queryStringActions.showProcessAction(actionId);
                            window.scroll({ top: 0, left: 0, behavior: "smooth" });
                        },
                    });
                }
            });
        });
        return Array.from(errors.values());
    }, [filteredSteps.steps, selectors, errorSelectors, queryStringActions]);
};
export function ProcessSidebarLayout(props: ProcessSidebarLayoutProps) {
    return <ProcessSidebarLayoutVNext {...props}/>;
}
export function ProcessSidebarLayoutVNext(props: ProcessSidebarLayoutProps) {
    const processSidebarRef = useRef<ProcessSidebarElement | null>(null);
    const isMobile = useIsMobile();
    const isDesktop = useIsDesktop();
    const { selectors, actions } = useProcessContext();
    const actionTemplates = useActionTemplatesFromContext();
    const { filteredSteps, actions: searchFilterActions, state: { searchFilter }, } = useProcessSearchFilterContext();
    const processType = selectors.getProcessType();
    const processErrorSelectors = useProcessErrorSelectors();
    const processErrors = useProcessErrors(filteredSteps, processErrorSelectors);
    const { actions: queryStringActions } = useProcessQueryStringContext();
    const runbookContext = useOptionalRunbookContext();
    const errorActions = useProcessErrorActions();
    const warningActions = useProcessWarningActions();
    const { isOpen: isProcessStepSorterDialogOpen, openDialog: openProcessStepSorterDialog } = useDialogTrigger();
    const { isOpen: isChildStepSorterDialogOpen, openDialog: openChildStepSorterDialog } = useDialogTrigger();
    const { isOpen: isCloneStepDialogOpen, openDialog: openCloneStepDialog } = useDialogTrigger();
    const { isOpen: isMoveStepDialogOpen, openDialog: openMoveStepDialog } = useDialogTrigger();
    const { isOpen: isDeleteStepDialogOpen, openDialog: openDeleteStepDialog } = useDialogTrigger();
    const [sortStepSource, setSortStepSource] = React.useState<{
        step: StoredStep;
    } | undefined>();
    const [cloneStepSource, setCloneStepSource] = React.useState<{
        step: StoredStep;
        action: StoredAction | undefined;
    } | undefined>();
    const [moveStepSource, setMoveStepSource] = React.useState<{
        step: StoredStep;
        action: StoredAction;
    } | undefined>();
    const [deleteStepSource, setDeleteStepSource] = React.useState<{
        step: StoredStep;
        action: StoredAction | undefined;
        arcWarning: React.ReactElement | undefined;
        isActive: boolean;
    } | undefined>();
    const currentScrollArea = useCurrentScrollArea();
    const onStepClick = () => {
        currentScrollArea.scroll({ top: 0, behavior: "instant" });
    };
    const onReorderChildSteps = (step: Readonly<StoredStep>) => {
        setSortStepSource({ step });
        openChildStepSorterDialog();
    };
    const onCloneStep = (step: Readonly<StoredStep>, action: Readonly<StoredAction> | undefined) => {
        setCloneStepSource({ step, action });
        openCloneStepDialog();
    };
    const onMoveStep = (step: Readonly<StoredStep>, action: Readonly<StoredAction>) => {
        setMoveStepSource({ step, action });
        openMoveStepDialog();
    };
    const onDeleteStep = (step: Readonly<StoredStep>, action: Readonly<StoredAction> | undefined, isActive: boolean) => {
        if (isProcessTemplateProcessIdentifier(props.processIdentifier) || !props.releaseCreationPackageStepId || isProjectTemplateProcessIdentifier(props.processIdentifier)) {
            setDeleteStepSource({ step, action, arcWarning: undefined, isActive });
        }
        else if (isTemplatedProjectProcessIdentifier(props.processIdentifier) || isProjectTemplateProcessIdentifier(props.processIdentifier)) {
            return;
        }
        else if (action) {
            const isArcAction = props.releaseCreationPackageStepId === action.Id;
            const arcWarning = isArcAction ? (<ArcWarningCallout parentStep={false} stepName={step.Name} triggersLink={links.triggersPage.generateUrl({ spaceId: props.processIdentifier.spaceId, projectSlug: props.processIdentifier.projectSlug })}/>) : undefined;
            setDeleteStepSource({ step, action, arcWarning, isActive });
        }
        else {
            const isParentOfArcAction = step.ActionIds.some((actionId) => actionId === props.releaseCreationPackageStepId);
            const stepName = selectors.getActionById(props.releaseCreationPackageStepId).Name;
            const arcWarning = isParentOfArcAction ? (<ArcWarningCallout parentStep={isParentOfArcAction} stepName={stepName} triggersLink={links.triggersPage.generateUrl({ spaceId: props.processIdentifier.spaceId, projectSlug: props.processIdentifier.projectSlug })}/>) : undefined;
            setDeleteStepSource({ step, action, arcWarning, isActive });
        }
        openDeleteStepDialog();
    };
    const cloneStepContext = {
        getRunbookProcess: (source: CloneRunbookProcessSource) => {
            return repository.RunbookProcess.get(source.runbook.RunbookProcessId);
        },
        getDeploymentProcess: (source: CloneDeploymentProcessStepsSource) => repository.DeploymentProcesses.get(source.project.Id),
        getProcessResource: (): DeploymentProcessResource | RunbookProcessResource => {
            const process = selectors.getProcessResource();
            if (isDeploymentProcessResource(process) || isRunbookProcessResource(process)) {
                return process;
            }
            throw new Error("Attempting to clone a step from an unsupported process type.");
        },
        getActionResource: selectors.getActionResource,
        getStepResource: selectors.getStepResource,
        setProcess: actions.setProcess,
        cloneToDifferentProcess: actions.cloneToDifferentProcess,
        setErrors: (errors: Errors) => errorActions.setErrors(errors, selectors),
        clearErrors: errorActions.clearErrors,
        setWarnings: (warnings: Warnings) => warningActions.setWarnings(warnings, selectors),
        clearWarnings: warningActions.clearWarnings,
    };
    const onAddChildStep = (step: Readonly<StoredStep>) => {
        processSidebarRef.current?.expandStep(step.Id);
    };
    const processSidebarStepData = useProcessSidebarStepData(props.processIdentifier, props.gitRefResource, onReorderChildSteps, onCloneStep, onMoveStep, onDeleteStep, onAddChildStep);
    const processSidebarMenuItems = useProcessSidebarMenuItems(openProcessStepSorterDialog);
    if (!selectors.hasValidProcess() || !actionTemplates) {
        return (<PageContent header={{ title: "" }} busy={true}>
                {null}
            </PageContent>);
    }
    return (<>
            <ProcessErrorSelectors>
                {(errorSelectors) => {
            const errorMessage = errorSelectors.getGlobalErrorMessage();
            return (<ProcessWarningSelectors>
                            {(warningSelectors) => {
                    const globalWarnings = warningSelectors.getGlobalWarnings();
                    return (<React.Fragment>
                                        {errorMessage && processType !== ProcessType.ProcessTemplate && (<ErrorPanel message={errorMessage} errors={errorSelectors.getGlobalErrors()} clickableErrors={processErrors} parsedHelpLinks={undefined} helpText={undefined} helpLink={undefined} statusCode={undefined} scrollToPanel={true}/>)}
                                        {globalWarnings.length > 0 && (<WarningPanel title={warningSelectors.getGlobalWarningMessage()} warnings={globalWarnings} parsedHelpLinks={undefined} helpText={undefined} helpLink={undefined} scrollToPanel={false}/>)}
                                    </React.Fragment>);
                }}
                        </ProcessWarningSelectors>);
        }}
            </ProcessErrorSelectors>
            {processType === ProcessType.Deployment && <Divider />}
            <div className={cx(stylesVNext.container, { [stylesVNext.containerMobile]: isMobile })}>
                {selectors.hasSteps() && (<div className={cx(stylesVNext.sidebar, { [stylesVNext.sidebarMobile]: isMobile, [stylesVNext.sidebarDesktop]: isDesktop })}>
                        <ProcessSidebar ref={processSidebarRef} steps={processSidebarStepData} menuItems={processSidebarMenuItems} searchValue={searchFilter.filterKeyword} onSearchValueChange={(val) => searchFilterActions.onFilterChange((prev) => ({ ...prev, filterKeyword: val }))} showStepNumbers={isDesktop} onStepClick={onStepClick}/>
                        <Dialog open={isProcessStepSorterDialogOpen}>
                            <StepSorter title="Reorder steps" initialItems={selectors.getAllSteps()} onComplete={(process) => {
                actions.reorderSteps(process.map((x) => x.Id));
                actions.resetPackageRequirementAfterPackageAcquisitionStep();
            }}/>
                        </Dialog>
                        {sortStepSource && (<Dialog open={isChildStepSorterDialogOpen}>
                                <StepSorter initialItems={selectors.getChildActions(sortStepSource.step.Id)} title={"Reorder child steps"} onComplete={(ordered) => {
                    actions.reorderChildActions(sortStepSource.step.Id, ordered.map((x) => x.Id));
                }}/>
                            </Dialog>)}
                        {cloneStepSource && (<Dialog open={isCloneStepDialogOpen}>
                                <CloneStep stepId={cloneStepSource.step.Id} actionId={cloneStepSource.action?.Id} currentRunbook={runbookContext?.state.runbook} actionName={cloneStepSource.action?.Name ?? cloneStepSource.step.Name} onCloneTargetSelected={(definition) => CloneStepAction(cloneStepContext, definition, cloneStepSource.step, cloneStepSource.action)}/>
                            </Dialog>)}
                        {moveStepSource && (<Dialog open={isMoveStepDialogOpen}>
                                <SelectParentStep steps={selectors.getAllSteps().filter((s) => s.Id !== moveStepSource.step.Id && selectors.canStepHaveChildren(s.Id))} actionName={moveStepSource.action.Name} 
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            currentlyTargetedRoles={moveStepSource.step.Properties[SpecialVariables.Action.TargetRoles] as string} onStepSelected={(parentStepId) => {
                    actions.moveActionIntoStep(moveStepSource.action.Id, parentStepId);
                }}/>
                            </Dialog>)}
                        {deleteStepSource && (<DeleteDialog title={`Are you sure you want to delete this ${deleteStepSource.action ? "step" : "parent step"}?`} open={isDeleteStepDialogOpen} deleteButtonDisabled={!!deleteStepSource.arcWarning} onDeleteClick={async () => deleteActionAndRedirect(deleteStepSource.step, deleteStepSource.action, deleteStepSource.isActive, actions, selectors, queryStringActions)} renderContent={() => <div>{deleteStepSource.arcWarning ? deleteStepSource.arcWarning : <p>Do you wish to continue?</p>}</div>}/>)}
                    </div>)}
                <div className={stylesVNext.content}>{props.render()}</div>
            </div>
        </>);
}
const stylesVNext = {
    container: css({
        display: "flex",
        flexDirection: "row",
        flex: 1,
        height: "100%",
    }),
    containerMobile: css({
        flexDirection: "column",
    }),
    sidebar: css({
        padding: `${space[16]} ${space[12]}`,
        backgroundColor: `rgb(from ${themeTokens.color.processWrapper.background.primary} r g b / 0.3)`,
        width: "17.5rem",
        flexShrink: 0,
        borderRight: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
    }),
    sidebarMobile: css({
        width: "auto",
        borderRight: "none",
    }),
    sidebarDesktop: css({
        width: "22.5rem",
    }),
    content: css({
        flex: 1,
    }),
    parentStepLogo: css({
        background: colorScales.blue[600],
        padding: space[1],
    }),
};
function useProcessSidebarStepData(processIdentifier: ProcessIdentifier, gitRefResource: GitRefResource | undefined, onReorderChildSteps: (step: Readonly<StoredStep>) => void, onCloneStep: (step: Readonly<StoredStep>, action: Readonly<StoredAction> | undefined) => void, onMoveStep: (step: Readonly<StoredStep>, action: Readonly<StoredAction>) => void, onDeleteStep: (step: Readonly<StoredStep>, action: Readonly<StoredAction> | undefined, isActive: boolean) => void, onAddChildStep: (step: Readonly<StoredStep>) => void): ProcessSidebarRootStep[] {
    const { selectors, actions } = useProcessContext();
    const { filteredSteps } = useProcessSearchFilterContext();
    const { state: { queryFilter }, } = useProcessQueryStringContext();
    const processTemplatesContext = useProcessTemplatesFromContext();
    const processErrorSelectors = useProcessErrorSelectors();
    const processWarningSelectors = useProcessWarningSelectors();
    const createProcessListItemContextMenu = useCreateProcessListItemContextMenu({
        processIdentifier,
        gitRefResource,
        onReorderChildSteps,
        onCloneStep,
        onMoveStep,
        onDeleteStep,
        onAddChildStep,
    });
    if (!selectors.hasSteps())
        return [];
    // TODO: This logic probably already exists somewhere,should find and reuse it
    const generateUrl = (stepParams: {
        actionId: string;
    } | {
        parentStepId: string;
    }) => {
        if (isDeploymentProcessIdentifier(processIdentifier) || isTemplatedProjectProcessIdentifier(processIdentifier)) {
            return gitRefResource
                ? links.branchDeploymentProcessEditorPage.generateUrl({ spaceId: processIdentifier.spaceId, projectSlug: processIdentifier.projectSlug, branchName: gitRefResource?.CanonicalName }, stepParams)
                : links.deploymentProcessEditorPage.generateUrl({ spaceId: processIdentifier.spaceId, projectSlug: processIdentifier.projectSlug }, stepParams);
        }
        if (isRunbookProcessIdentifier(processIdentifier)) {
            return links.runbookProcessEditorPage.generateUrl({ spaceId: processIdentifier.spaceId, projectSlug: processIdentifier.projectSlug, runbookId: processIdentifier.runbookId, processId: processIdentifier.id }, stepParams);
        }
        if (isProjectTemplateProcessIdentifier(processIdentifier)) {
            return links.projectTemplateDeploymentProcessEditorPage.generateUrl({ slug: processIdentifier.slug, gitRef: processIdentifier.gitRef }, stepParams);
        }
        return links.editProcessTemplatePage.generateUrl({ slug: processIdentifier.slug }, { ...stepParams, gitRef: gitRefResource?.CanonicalName });
    };
    function mapProcessTemplate(step: StoredStep, action: StoredAction): ProcessSidebarRootStep {
        const processTemplateSlug = getProcessTemplateSlugFromAction(action);
        const processTemplateVersionMask = getProcessTemplateVersionMaskFromAction(action);
        const containingProcessTemplateStep = selectors.getStepById(action.ParentId);
        const containingProcessTemplateStepNumber = selectors.getStepNumber(containingProcessTemplateStep.Id);
        const processTemplate = processTemplatesContext.find((b) => isMatchingProcessTemplate(b, processTemplateSlug, processTemplateVersionMask))?.processTemplate;
        const isActionDisabled = selectors.isActionDisabled(action.Id);
        const isSelected = queryFilter.actionId === action.Id;
        const baseStepData: ProcessSidebarRootStep = {
            id: action.Id,
            stepNumber: containingProcessTemplateStepNumber,
            name: containingProcessTemplateStep.Name,
            href: generateUrl({ actionId: action.Id }),
            logo: {
                src: getActionLogoUrl(action),
            },
            isSelected,
            isDisabled: isActionDisabled,
            errors: processErrorSelectors.getProcessTemplateUsageErrors(step.Id),
            warnings: processWarningSelectors.getActionWarnings(action.Id, selectors),
            isRunInParallelWithPreviousStep: false,
            menuItems: createProcessListItemContextMenu({ step, action, isSelected }),
        };
        if (processTemplate === "NotFound") {
            return {
                ...baseStepData,
                errors: processTemplateSlug ? [...baseStepData.errors, "Process template not found"] : baseStepData.errors,
            };
        }
        if (!processTemplate) {
            return baseStepData;
        }
        return {
            ...baseStepData,
            errors: processErrorSelectors.getProcessTemplateUsageErrors(step.Id),
            containerLabel: "Template",
            containerSteps: processTemplate.Steps.map((processTemplateStep, processTemplateStepIndex) => {
                if (processTemplateStep.Actions.length === 1) {
                    const processTemplateAction = processTemplateStep.Actions[0];
                    return {
                        id: processTemplateAction.Id,
                        name: processTemplateAction.Name,
                        href: generateUrl({ actionId: action.Id }),
                        logo: {
                            src: getActionLogoUrl(processTemplateAction),
                        },
                        isSelected: false,
                        isDisabled: isActionDisabled || processTemplateAction.IsDisabled,
                        errors: processErrorSelectors.getProcessTemplateActionErrors(containingProcessTemplateStep.Name, processTemplateStepIndex, 0, false),
                        warnings: [],
                        isRunInParallelWithPreviousStep: processTemplateStep.StartTrigger === StartTrigger.StartWithPrevious,
                        menuItems: [],
                    };
                }
                else {
                    const maxParallelism = processTemplateStep.Properties[SpecialVariables.Action.MaxParallelism];
                    const showWindowSize = maxParallelism && maxParallelism.toString().length > 0;
                    return {
                        id: processTemplateStep.Id,
                        name: processTemplateStep.Name,
                        href: generateUrl({ actionId: action.Id }),
                        logo: <StepRolling height="1.5rem" className={stylesVNext.parentStepLogo}/>,
                        isSelected: false,
                        isDisabled: isActionDisabled,
                        errors: processErrorSelectors.getProcessTemplateStepErrors(containingProcessTemplateStep.Name, processTemplateStepIndex),
                        warnings: [],
                        isRunInParallelWithPreviousStep: processTemplateStep.StartTrigger === StartTrigger.StartWithPrevious,
                        menuItems: [],
                        containerLabel: showWindowSize ? "Rolling" : undefined,
                        containerSteps: processTemplateStep.Actions.map((processTemplateAction, processTemplateActionIndex) => {
                            return {
                                id: processTemplateAction.Id,
                                name: processTemplateAction.Name,
                                href: generateUrl({ actionId: action.Id }),
                                logo: {
                                    src: getActionLogoUrl(processTemplateAction),
                                },
                                isSelected: false,
                                isDisabled: isActionDisabled || processTemplateAction.IsDisabled,
                                errors: processErrorSelectors.getProcessTemplateActionErrors(containingProcessTemplateStep.Name, processTemplateStepIndex, processTemplateActionIndex, true),
                                warnings: [],
                                isRunInParallelWithPreviousStep: false,
                                menuItems: [],
                            };
                        }),
                    };
                }
            }),
        };
    }
    function mapSingleStep(step: StoredStep, action: StoredAction): ProcessSidebarRootStep {
        const isSelected = queryFilter.actionId === action.Id;
        return {
            id: action.Id,
            name: action.Name,
            stepNumber: selectors.getStepNumber(step.Id),
            href: generateUrl({ actionId: action.Id }),
            logo: {
                src: getActionLogoUrl(action),
            },
            isSelected,
            isDisabled: action.IsDisabled,
            errors: processErrorSelectors.getActionErrors(action.Id, selectors),
            warnings: processWarningSelectors.getActionWarningSummaries(action.Id, selectors),
            isRunInParallelWithPreviousStep: step.StartTrigger === StartTrigger.StartWithPrevious && !selectors.isFirstStep(step.Id),
            menuItems: createProcessListItemContextMenu({ step, action, isSelected }),
        };
    }
    function mapParentStep(step: StoredStep): ProcessSidebarRootStep {
        const maxParallelism = step.Properties[SpecialVariables.Action.MaxParallelism];
        const showWindowSize = maxParallelism && maxParallelism.toString().length > 0;
        const isSelected = queryFilter.parentStepId === step.Id;
        return {
            id: step.Id,
            name: step.Name,
            stepNumber: selectors.getStepNumber(step.Id),
            href: generateUrl({ parentStepId: step.Id }),
            logo: <StepRolling height="1.5rem" className={stylesVNext.parentStepLogo}/>,
            isSelected,
            isDisabled: selectors.isStepDisabled(step.Id),
            errors: processErrorSelectors.getStepErrors(step.Id),
            warnings: processWarningSelectors.getStepWarningSummaries(step.Id),
            isRunInParallelWithPreviousStep: step.StartTrigger === StartTrigger.StartWithPrevious && !selectors.isFirstStep(step.Id),
            menuItems: createProcessListItemContextMenu({ step, action: undefined, isSelected }),
            containerLabel: showWindowSize ? "Rolling" : undefined,
            containerSteps: selectors.getChildActions(step.Id).map((childAction) => mapChildStep(step, childAction)),
        };
    }
    function mapChildStep(step: StoredStep, childAction: StoredAction): ProcessSidebarStepActionData {
        const isSelected = queryFilter.actionId === childAction.Id;
        return {
            id: childAction.Id,
            name: childAction.Name,
            href: generateUrl({ actionId: childAction.Id }),
            logo: {
                src: getActionLogoUrl(childAction),
            },
            isSelected,
            isDisabled: childAction.IsDisabled,
            errors: processErrorSelectors.getActionErrors(childAction.Id, selectors),
            warnings: processWarningSelectors.getActionWarningSummaries(childAction.Id, selectors),
            isRunInParallelWithPreviousStep: false,
            menuItems: createProcessListItemContextMenu({ step, action: childAction, isSelected }),
        };
    }
    return filteredSteps.steps
        .filter((x) => x.filtered)
        .map(({ step: filteredStep }) => {
        const step = selectors.getStepById(filteredStep.Id);
        if (step.ActionIds.length === 1) {
            const action = selectors.getActionById(step.ActionIds[0]);
            if (action.ActionType === "Octopus.ProcessTemplate") {
                return mapProcessTemplate(step, action);
            }
            return mapSingleStep(step, action);
        }
        return mapParentStep(step);
    });
}
